import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import styles from "./Text.module.css";

/**
 * Text — typography primitive.
 *
 * Wraps a semantic HTML element with one of the Figma typography tokens.
 * `size` maps to --text-size-{xs..xxl} and --text-lh-{xs..xxl} together;
 * `weight` maps to --text-weight-{regular,medium,semibold}; `tone` maps
 * to the --text-{primary,secondary,tertiary,link,negative,success,on-dark}
 * color tokens.
 *
 * When you want the 18 pre-assembled composite shorthands (H1/H2/H3,
 * Subtitle, Body, Micro × 3 weights), use `font: var(--text-h1-semibold)`
 * in a CSS Module directly. Text is for the atomic properties.
 */

type Size = "xs" | "s" | "m" | "l" | "xl" | "xxl";
type Weight = "regular" | "medium" | "semibold";
type Tone = "primary" | "secondary" | "tertiary" | "link" | "negative" | "success" | "on-dark";

type TextElement = "p" | "span" | "div" | "h1" | "h2" | "h3" | "h4" | "label" | "strong" | "em";

/**
 * NOTE on `as` polymorphism: same simplification as `BoxProps`.
 *
 * `TextProps` extends the attribute set of `<span>`, so `<Text as="label" htmlFor="…">`
 * compiles but does NOT type-check `htmlFor` as a label attribute.
 * Works at runtime because the prop is spread onto the underlying element,
 * but the TypeScript checker won't narrow to the element-specific attrs.
 *
 * If you need element-specific attributes (htmlFor on a label, etc.),
 * use the native element directly or widen the type at the call site
 * with a cast.
 */
export type TextProps = {
  /** Element to render. Defaults to `span` (inline). */
  as?: TextElement;
  /** Font size. Matches Figma Size/xs..xxl (12/14/16/18/20/24 px). */
  size?: Size;
  /** Font weight. */
  weight?: Weight;
  /** Color. Maps to --text-* semantic tokens. */
  tone?: Tone;
  /** Single-line ellipsis truncation. Works on the default `<span>` via
   *  `display: inline-block` — parent must still bound the width. */
  truncate?: boolean;
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<"span">, "className" | "children">;

export function Text({
  as,
  size = "m",
  weight = "regular",
  tone = "primary",
  truncate,
  className,
  children,
  ...rest
}: TextProps) {
  const Component = (as ?? "span") as ElementType;
  const classes = [
    styles.text,
    styles[`size-${size}`],
    styles[`weight-${weight}`],
    styles[`tone-${tone}`],
    truncate ? styles.truncate : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
